import { useIdsSelect, useIdsSelecting } from '@entities/common/IdsSelector/models';
import type { ForFunctionsToReduceSelectedIds } from '@entities/common/IdsSelector/types';
import { memo, useCallback, useEffect } from 'react';
import { IconCheckbox } from '@shared/ui/inputs/iconCheckbox/index';
import type { IconCheckboxPT } from '@shared/ui/inputs/iconCheckbox/ui';

type PT = ForFunctionsToReduceSelectedIds &
  IconCheckboxPT & {
    onSelect?: () => void;
    onUnSelect?: () => void;
    shouldBeSelectOne?: boolean;
  };

/**
 * SelectingIdsIconCheckbox - чекбокс который нужен для выбора айди этой сущности и
 * сохранения этого айди в rtk/common/idsSelector/selectedIds[name]
 *
 * @param name {string} - имя в обьекте selectedIds[name].
 *
 * Массив, на который ссылается этот идентификатор (selectedIds[name]) содержит выбранные айди.
 */

export const SelectingIdsIconCheckbox = memo(
  ({ id, name, onSelect, onUnSelect, shouldBeSelectOne, ...props }: PT) => {
    const { isSelecting, isNeedToSelectAll } = useIdsSelecting(name);

    const {
      doAddId,
      doRemoveId,
      isIdSelected,
      doResetSelectedIds,
      doAddExcludedId,
      doRemoveExcludedId,
      excludedIds,
    } = useIdsSelect(name);

    const isCurrentIdSelected = isIdSelected(id);

    const doSelect = useCallback(() => {
      onSelect?.();

      if (isNeedToSelectAll) {
        doRemoveExcludedId(id);
      }

      shouldBeSelectOne ? doResetSelectedIds([id]) : doAddId(id);
    }, [
      id,
      onSelect,
      doAddId,
      shouldBeSelectOne,
      doResetSelectedIds,
      doRemoveExcludedId,
      isNeedToSelectAll,
    ]);

    const doUnSelect = useCallback(() => {
      onUnSelect?.();

      if (isNeedToSelectAll) {
        doAddExcludedId(id);
      }
      doRemoveId(id);
    }, [id, onUnSelect, doRemoveId, doAddExcludedId, isNeedToSelectAll]);

    useEffect(() => {
      if (
        !Boolean(shouldBeSelectOne) &&
        isNeedToSelectAll &&
        !isCurrentIdSelected &&
        !excludedIds?.includes(id)
      ) {
        doAddId(id);
      }
    }, [shouldBeSelectOne, isNeedToSelectAll, isCurrentIdSelected, id, doAddId, excludedIds]);

    return (
      <>
        {isSelecting && (
          <IconCheckbox
            checked={isCurrentIdSelected}
            onClick={(e) => {
              e.stopPropagation();
              if (props.disabled) {
                return;
              }
              isCurrentIdSelected ? doUnSelect() : doSelect();
            }}
            {...props}
          />
        )}
      </>
    );
  }
);
